import { useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { T } from "./tokens";
import { HsAdminGate } from "./HsAdminGate";
import {
    summaryValue,
    useActiveConsultants,
    useVisitsByConsultant,
    scoreConsultantPeriod,
    toISODate,
    addDays,
    sameDay,
} from "./data";
import { availablePoints, fieldDaysPerMonth, fmtPoints } from "./points";
import { usePointsConfig } from "./pointsConfig";
import type { WeeklyVisitSummary, VisitConsultantT, SfVisit } from "./data";
import { daysUtilisation, dayExceptionFor } from "./data";

export const initials = (name?: string) =>
    (name || "?")
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();

// Deliberately NOT a RAG band. There is no agreed utilisation target, so
// colouring 70/95 boundaries would render a verdict nobody set.
export const utilColour = (util: number | null | undefined) => (util == null ? T.muted2 : T.purple);

const Avatar = ({ name, size = 30 }: { name?: string; size?: number }) => (
    <Box
        sx={{
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: "50%",
            background: T.ink,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: `${size * 0.34}px`,
            fontWeight: 700,
            flexShrink: 0,
        }}
    >
        {initials(name)}
    </Box>
);

// Two of the five columns are points, so the track list follows the flag.
const gridFor = (withPoints: boolean) =>
    withPoints ? "1.6fr 146px 146px 74px 74px" : "1.6fr 146px 74px";

type SortKey = "name" | "util" | "utilPts" | "visits" | "points";

interface ConsultantRow {
    teamName: string;
    c: VisitConsultantT;
    /** The long-standing day-count measure, over the summary's own ~4-week
     *  window — NOT the period selected in the top bar. */
    util: number | null;
    /** Capacity points against points available for the SELECTED period. */
    utilPts: number | null;
    /** Visits in the SELECTED period. null while the query is in flight. */
    visits: number | null;
    /** Recognition points in the SELECTED period. */
    points: number | null;
    /** Visits with no On Site / Remote flag, scored as on-site. */
    modeAssumed: number;
    /** No row in the weekly summary at all — nothing booked in its window, but
     *  the window is ~4 weeks, so this is not "never works". */
    noSummary: boolean;
}

const ConsultantsScreen = ({
    data,
    isLoading,
    isError,
    team = "AllTeams",
    consultant = "AllConsultants",
    dates,
    initialNothingBookedOnly,
    onOpen,
}: {
    data: WeeklyVisitSummary | null | undefined;
    isLoading: boolean;
    isError: boolean;
    team?: string;
    consultant?: string;
    dates: Date[];
    /** Set when Overview sent the user here to look at empty diaries. */
    initialNothingBookedOnly?: boolean;
    onOpen: (teamName: string, c: VisitConsultantT) => void;
}) => {
    // Hooks must all run before any early return, or React sees a different
    // number of them between renders and throws.
    const { config, showPoints } = usePointsConfig();
    // Switched-off consultants are out of the list and out of every total.
    const roster = useActiveConsultants();
    const [query, setQuery] = useState("");
    const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: "name", desc: false });
    // A sort left on a points column would silently order the table by something
    // no longer on screen, so it falls back to the name.
    const effectiveSort =
        !showPoints && (sort.key === "points" || sort.key === "utilPts")
            ? { key: "name" as SortKey, desc: false }
            : sort;
    const [showAll, setShowAll] = useState(false);
    const [nothingBookedOnly, setNothingBookedOnly] = useState(!!initialNothingBookedOnly);

    // Decided before the queries below, so a narrowed filter does not still fetch
    // visits for everyone. Falls back to the summary if the roster is unavailable.
    const inScope: Array<{ email: string; teamName: string }> = roster.active.length
        ? roster.active
              .filter((rc) => team === "AllTeams" || (rc.team || "Unknown") === team)
              .filter(
                  (rc) =>
                      consultant === "AllConsultants" ||
                      (rc.email || "").toLowerCase() === consultant.toLowerCase(),
              )
              .map((rc) => ({ email: rc.email || "", teamName: rc.team || "No team" }))
        : (data?.regions || [])
              .flatMap((r) =>
                  (r.consultants || [])
                      .filter(
                          (c) =>
                              consultant === "AllConsultants" ||
                              (c.email || "").toLowerCase() === consultant.toLowerCase(),
                      )
                      .map((c) => ({ email: c.email || "", teamName: "No team" })),
              );

    // Same query keys and period as the schedule and report, so this shares their
    // cache and the numbers match.
    const from = toISODate(dates[0]);
    const to = toISODate(addDays(dates[dates.length - 1], 1));
    const periodQuery = useVisitsByConsultant(from, to);

    // Points available in this period — 15 field days at 2 points a day for a
    // month, pro-rated by field days per year for a week. Identical to Reports.
    const isWeek = dates.length <= 5;
    const periodFieldDays = isWeek ? config.fieldDaysPerYear / 52 : fieldDaysPerMonth(config);
    const periodPoints = availablePoints(periodFieldDays, config);

    if (isLoading)
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: "48px" }}>
                <CircularProgress size={26} />
            </Box>
        );
    if (isError) return <Box sx={{ p: "28px", color: T.muted }}>Couldn't load consultant data.</Box>;
    if (!data) return <HsAdminGate />;

    // Rows come from the ROSTER, not from whoever has a visit — the summary only
    // holds consultants with bookings, which hid the empty diaries.
    const summaryByEmail = new Map<string, VisitConsultantT>();
    (data.regions || []).forEach((r) =>
        (r.consultants || []).forEach((c) => {
            if (c.email) summaryByEmail.set(c.email.toLowerCase(), c);
        }),
    );
    const rosterByEmail = new Map(roster.active.map((rc) => [(rc.email || "").toLowerCase(), rc]));

    const allRows: ConsultantRow[] = inScope.map(({ email, teamName }, i) => {
        const key = email.toLowerCase();
        const summary = summaryByEmail.get(key);
        const rc = rosterByEmail.get(key);
        const c =
            summary ??
            ({
                name: rc?.name || email,
                email,
                jobTitle: rc?.position || undefined,
                data: [],
            } as unknown as VisitConsultantT);

        // Only real data counts as loaded — treating an error as loaded would
        // turn a timeout into "0% utilised" against a named person.
        const visits = periodQuery.byEmail.get((email || "").toLowerCase());
        const periodVisits = (visits || []).filter(
            (v) => v.visitStartDate && dates.some((d) => sameDay(new Date(v.visitStartDate as string), d)),
        );
        const scored = visits ? scoreConsultantPeriod(periodVisits, config) : null;

        return {
            teamName,
            c,
            util: visits ? daysUtilisation(dates, periodVisits, (d) => dayExceptionFor(c, d)) : null,
            utilPts: scored == null ? null : Math.round((scored.capacity / periodPoints) * 100),
            // Both counts come from the selected period, so they sit alongside
            // the points measure rather than the summary's own window.
            visits: visits ? periodVisits.length : null,
            points: scored?.points ?? null,
            modeAssumed: scored?.modeAssumed ?? 0,
            noSummary: !summary,
        };
    });

    const q = query.trim().toLowerCase();
    const searched = q
        ? allRows.filter(
              (r) =>
                  (r.c.name || "").toLowerCase().includes(q) ||
                  (r.c.email || "").toLowerCase().includes(q) ||
                  (r.teamName || "").toLowerCase().includes(q),
          )
        : allRows;

    // A FILTER, not a pinned group: pinning overrode the sort and, with the
    // ten-row cap, hid the rest of the team entirely.
    const nothingBookedCount = searched.filter((r) => r.noSummary).length;
    const rows = nothingBookedOnly ? searched.filter((r) => r.noSummary) : searched;

    const cmp = (a: ConsultantRow, b: ConsultantRow) => {
        const dir = effectiveSort.desc ? -1 : 1;
        switch (effectiveSort.key) {
            case "util":
                return ((a.util ?? -1) - (b.util ?? -1)) * dir;
            case "utilPts":
                return ((a.utilPts ?? -1) - (b.utilPts ?? -1)) * dir;
            case "visits":
                return ((a.visits ?? -1) - (b.visits ?? -1)) * dir;
            case "points":
                return ((a.points ?? -1) - (b.points ?? -1)) * dir;
            default:
                return (a.c.name || "").localeCompare(b.c.name || "") * dir;
        }
    };

    // One list, one sort order.
    const ordered = [...rows].sort(cmp);
    const shown = showAll ? ordered : ordered.slice(0, 10);

    const toggleSort = (key: SortKey) =>
        setSort((s) =>
            s.key === key
                ? { key, desc: !s.desc }
                : // Numbers are most useful highest-first; names A→Z.
                  { key, desc: key !== "name" },
        );

    const HeadCell = ({
        label,
        sortKey,
        tip,
        align = "left",
    }: {
        label: string;
        sortKey: SortKey;
        tip?: string;
        align?: "left" | "right";
    }) => (
        <Box
            component="button"
            onClick={() => toggleSort(sortKey)}
            title={tip}
            sx={{
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
                fontFamily: T.font,
                display: "flex",
                alignItems: "center",
                justifyContent: align === "right" ? "flex-end" : "flex-start",
                gap: "4px",
                color: effectiveSort.key === sortKey ? T.purpleDeep : T.muted,
                "&:hover": { color: T.purpleDeep },
            }}
        >
            <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "inherit" }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: "9px", color: "inherit", opacity: effectiveSort.key === sortKey ? 1 : 0.35 }}>
                {effectiveSort.key === sortKey ? (effectiveSort.desc ? "▼" : "▲") : "▲"}
            </Typography>
        </Box>
    );

    const UtilCell = ({ pct, colour, unknown }: { pct: number | null; colour: string; unknown: string }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Box sx={{ flexGrow: 1, height: "9px", borderRadius: "5px", background: T.rowTint, overflow: "hidden" }}>
                <Box sx={{ width: `${Math.min(100, pct ?? 0)}%`, height: "100%", background: colour }} />
            </Box>
            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: pct == null ? T.muted2 : T.ink2, width: "36px", textAlign: "right", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                {pct == null ? unknown : `${pct}%`}
            </Typography>
        </Box>
    );

    const Row = ({ r }: { r: ConsultantRow }) => (
        <Box
            onClick={() => onOpen(r.teamName, r.c)}
            sx={{
                display: "grid",
                gridTemplateColumns: gridFor(showPoints),
                alignItems: "center",
                gap: "14px",
                padding: "7px 12px",
                borderTop: `1px solid ${T.borderSoft}`,
                cursor: "pointer",
                "&:hover": { background: T.rowTint },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                <Avatar name={r.c.name} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 700, color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {r.c.name}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: r.noSummary ? T.amber : T.muted, fontWeight: r.noSummary ? 700 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {r.noSummary
                            ? "Nothing booked"
                            : `${r.teamName}${r.c.jobTitle ? ` · ${r.c.jobTitle}` : ""}`}
                    </Typography>
                </Box>
            </Box>

            {/* Days — the long-standing measure. "—" when the summary has no
                figure; never 0, which would read as a measured fact. */}
            <UtilCell pct={r.util} colour={T.border} unknown="—" />

            {/* Points — the new measure. "…" while the visits are still loading. */}
            {showPoints && <UtilCell pct={r.utilPts} colour={T.purple} unknown="…" />}

            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: r.visits == null ? T.muted2 : T.ink2, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {r.visits == null ? "…" : r.visits}
            </Typography>

            {showPoints && (
            <Typography
                title={
                    r.modeAssumed
                        ? `${r.modeAssumed} of these visits have no On Site / Remote flag in Salesforce and were scored as on-site`
                        : undefined
                }
                sx={{ fontSize: "13px", fontWeight: 800, color: r.points == null ? T.muted2 : T.purpleDeep, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
            >
                {r.points == null ? "…" : fmtPoints(r.points)}
                {/* A pill, not a suffix — "~7" alongside a number read as
                    "minus 7". Background and gap make it obviously a tag. */}
                {!!r.modeAssumed && (
                    <Box
                        component="span"
                        sx={{
                            display: "inline-block",
                            ml: "6px",
                            padding: "0 4px",
                            borderRadius: "4px",
                            background: T.amberTint,
                            border: `1px solid ${T.amberBorder}`,
                            fontSize: "9px",
                            lineHeight: "14px",
                            color: T.amber,
                            fontWeight: 700,
                            verticalAlign: "1px",
                        }}
                    >
                        ⚑{r.modeAssumed}
                    </Box>
                )}
            </Typography>
            )}
        </Box>
    );

    return (
        <Box sx={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: "14px" }}>
            {/* Search first, because "show me Sarah" is the other job this page
                has to do besides "who needs attention". */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <Box
                    component="input"
                    value={query}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                    placeholder="Search name, email or team…"
                    sx={{
                        flexGrow: 1,
                        minWidth: "220px",
                        maxWidth: "340px",
                        border: `1px solid ${T.border}`,
                        borderRadius: "8px",
                        padding: "8px 12px",
                        fontSize: "13px",
                        fontFamily: T.font,
                        color: T.ink2,
                        background: T.card,
                        outline: "none",
                        "&:focus": { borderColor: T.purple },
                    }}
                />
                {/* A filter, so it composes with search and sort instead of
                    overriding the order. */}
                <Box
                    component="button"
                    onClick={() => {
                        setNothingBookedOnly((v) => !v);
                        setShowAll(false);
                    }}
                    disabled={nothingBookedCount === 0 && !nothingBookedOnly}
                    sx={{
                        border: `1px solid ${nothingBookedOnly ? T.amber : T.border}`,
                        background: nothingBookedOnly ? T.amberTint : T.card,
                        color: nothingBookedOnly ? T.amber : nothingBookedCount ? T.ink2 : T.muted2,
                        borderRadius: "8px",
                        padding: "8px 12px",
                        fontSize: "12px",
                        fontWeight: 700,
                        cursor: nothingBookedCount === 0 && !nothingBookedOnly ? "default" : "pointer",
                        fontFamily: T.font,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                    }}
                >
                    {nothingBookedOnly ? "✕ " : ""}Nothing booked · {nothingBookedCount}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: "14px", ml: "auto" }}>
                    <Typography sx={{ fontSize: "11px", color: T.muted, whiteSpace: "nowrap" }}>
                        <Box component="span" sx={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "3px", background: T.border, mr: "5px", verticalAlign: "middle" }} />
                        Days
                        <Box component="span" sx={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "3px", background: T.purple, ml: "12px", mr: "5px", verticalAlign: "middle" }} />
                        Points
                    </Typography>
                    <Typography sx={{ fontSize: "11.5px", color: T.muted, whiteSpace: "nowrap" }}>
                        {rows.length} of {allRows.length} consultants
                        {!roster.active.length && " · roster unavailable"}
                    </Typography>
                </Box>
            </Box>

            {rows.length === 0 ? (
                <Box sx={{ p: "28px", color: T.muted, fontSize: "13px" }}>
                    {nothingBookedOnly
                        ? "Everyone in this selection has bookings in the summary's window."
                        : q
                          ? `No consultant matches “${query}”.`
                          : team === "AllTeams" ? "No consultants." : `No consultants in ${team}.`}
                </Box>
            ) : (
                <Box sx={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: "12px", overflow: "hidden" }}>
                    {/* Sortable column heads */}
                    <Box sx={{ display: "grid", gridTemplateColumns: gridFor(showPoints), gap: "14px", padding: "9px 12px", background: T.rowTint }}>
                        <HeadCell label="Consultant" sortKey="name" />
                        <HeadCell
                            label="Util · days"
                            sortKey="util"
                            tip="Days used (a visit, admin or training) against working days for the selected period, with holiday and sickness taken out. A half-day visit still uses the day."
                        />
                        {showPoints && (
                            <HeadCell
                                label="Util · points"
                                sortKey="utilPts"
                                tip={`Capacity points against ${periodPoints} available for the selected period. Prices a half-day visit as half a day.`}
                            />
                        )}
                        <HeadCell
                            label="Visits"
                            sortKey="visits"
                            tip="Visits in the selected period — the number behind the old visit-count basis."
                            align="right"
                        />
                        {showPoints && (
                            <HeadCell
                                label="Points"
                                sortKey="points"
                                tip="Recognition points in the selected period — the number behind Util · points."
                                align="right"
                            />
                        )}
                    </Box>

                    {shown.map((r) => (
                        <Row key={r.c.email || r.c.name} r={r} />
                    ))}

                    {ordered.length > 10 && (
                        <Box
                            component="button"
                            onClick={() => setShowAll((v) => !v)}
                            sx={{
                                border: "none",
                                borderTop: `1px solid ${T.borderSoft}`,
                                background: T.rowTint,
                                color: T.purpleDeep,
                                width: "100%",
                                fontSize: "11.5px",
                                fontWeight: 700,
                                cursor: "pointer",
                                fontFamily: T.font,
                                padding: "10px 0",
                                display: "block",
                            }}
                        >
                            {showAll ? "▲ Show top 10 only" : `▼ Show all ${ordered.length}`}
                        </Box>
                    )}
                </Box>
            )}

        </Box>
    );
};

export default ConsultantsScreen;
